import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { band, CRITERIA, REASON_LABEL, type ResultDetail, type ResultRow } from '../lib/results';
import { bandTier } from '../components/Band';
import { Icon } from '../components/Icon';
import { firstName } from '../lib/today';
import { formatDate } from '../lib/when';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { useAuth } from '../store/auth';
import { WritingMarker } from '../components/WritingMarker';
import { clearsAt, copyScript, wordCount } from '../lib/scripts';

/**
 * Set as a script is marked and read after the reload, so the next screen can
 * say "marked" and offer the next script. Only a result id; see the privacy
 * notice's line on session storage.
 */
const JUST_MARKED = 'target.justMarked';

/** The question-by-question review, shown to the instructor and to the student. */
export function ResultDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const role = useAuth((s) => s.user?.role);
  const isStudent = role === 'STUDENT';

  const [result, setResult] = useState<ResultDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyWrong, setOnlyWrong] = useState(false);
  /** Which task was last copied, so its button can say so for a moment. */
  const [copied, setCopied] = useState<number | null>(null);

  async function copyMine(task: number, text: string) {
    if (await copyScript(text, document.getElementById(`my-essay-${task}`))) {
      setCopied(task);
      window.setTimeout(() => setCopied((c) => (c === task ? null : c)), 2_000);
    }
  }

  /**
   * The moment after marking: what was given, and the next script waiting —
   * so a pile of thirty is worked through in one go rather than by going back
   * to the list thirty times.
   */
  const [cheer, setCheer] = useState<{ next: string | null; left: number } | null>(null);
  useEffect(() => {
    setCheer(null);
    if (isStudent) return;
    let marked: string | null = null;
    try {
      marked = sessionStorage.getItem(JUST_MARKED);
      if (marked === id) sessionStorage.removeItem(JUST_MARKED);
    } catch {
      // Without storage there is simply no celebration.
    }
    if (marked !== id) return;
    api
      .get<{ results: ResultRow[] }>('/results', { params: { sort: 'graded', direction: 'asc', limit: 200 } })
      .then(({ data }) => {
        const waiting = data.results.filter((r) => r.awaitingMarking && r.id !== id);
        setCheer({ next: waiting[0]?.id ?? null, left: waiting.length });
      })
      .catch(() => setCheer({ next: null, left: 0 }));
  }, [id, isStudent]);

  useEffect(() => {
    api
      .get<{ result: ResultDetail }>(`/results/${id}`)
      .then(({ data }) => setResult(data.result))
      .catch((err) => setError(errorMessage(err, 'Could not load this result')));
  }, [id]);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!result) return <p className="muted">Loading…</p>;

  /**
   * The two lists this page is built from, taken as lists whatever arrives.
   *
   * A candidate's band score is not the place to be fragile. One field of an
   * unexpected shape — an older row whose breakdown was never filled in, a
   * paper marked before part scores existed — threw inside a render, React
   * unmounted the tree, and the page went white: no score, no answers, no
   * message, nothing to do but sign out. A missing breakdown should cost the
   * breakdown, not the result.
   */
  /* Offered only where the centre has review switched on; the server refuses
     it otherwise, and a link that leads to a refusal is worse than no link. */
  const canReview = result.reviewEnabled === true;
  const reviewLink = (
    <Link className="btn" to={isStudent ? `/my/results/${id}/review` : `/results/${id}/review`}>
      Go over the paper
    </Link>
  );
  const perPart = Array.isArray(result.perPart) ? result.perPart : [];
  const allMarks = Array.isArray(result.marks) ? result.marks : [];
  const marks = onlyWrong ? allMarks.filter((m) => !m.correct) : allMarks;
  const wrongCount = allMarks.filter((m) => !m.correct).length;
  const isWriting = result.module === 'WRITING';

  return (
    <>
      <PageHeader
        eyebrow={<Link to={isStudent ? '/my/results' : '/results'}>← Results</Link>}
        title={result.test.title}
        meta={
          <>
            <p className="muted">
              {isStudent ? 'You' : result.student.fullName} ·{' '}
              <span className="mono">{result.student.candidateNumber ?? '—'}</span>
            </p>
            {result.session.autoSubmitted && (
              <span className="tag">Submitted automatically when time ran out</span>
            )}
          </>
        }
      />

      <div className="stack">
        {cheer && (
          <section className="cheer" role="status">
            <span className="cheer__icon" aria-hidden="true">
              <Icon name="check" size={22} />
            </span>
            <div className="cheer__text">
              <p className="cheer__title">
                Marked — {firstName(result.student.fullName)} gets {band(result.bandScore)}.
              </p>
              <p className="cheer__body">
                {cheer.next
                  ? `${cheer.left} more ${cheer.left === 1 ? 'script is' : 'scripts are'} waiting.`
                  : 'That was the last one. Everything handed in is marked.'}
              </p>
            </div>
            {cheer.next ? (
              <Link className="btn btn--primary" to={`/results/${cheer.next}`}>
                Next script <Icon name="arrowRight" size={15} />
              </Link>
            ) : (
              <Link className="btn" to="/today">
                Back to Today
              </Link>
            )}
          </section>
        )}

        <section className="panel">
          <div className="scoreline">
            <div>
              <p className="eyebrow">{result.awaitingMarking ? 'Not yet marked' : 'Band'}</p>
              <p
                className={`scoreline__band mono${
                  result.bandScore === null ? '' : ` scoreline__band--${bandTier(result.bandScore)}`
                }`}
              >
                {band(result.bandScore)}
              </p>
            </div>
            {!isWriting && (
              <div>
                <p className="eyebrow">Raw score</p>
                <p className="scoreline__raw mono">
                  {result.rawScore}
                  <span className="muted">/{result.total}</span>
                </p>
              </div>
            )}
            {isWriting && Array.isArray(result.writingMarks) && (
              <div>
                <p className="eyebrow">By task</p>
                <ul className="partbars">
                  {result.writingMarks.map((m) => (
                    <li key={m.task}>
                      <span className="mono">{m.task}</span>
                      <span className="muted">
                        {m.task === 2 ? 'counts double' : 'counts once'}
                      </span>
                      <span className="mono">{band(m.band)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Hidden for Writing, which has tasks rather than parts, and for a
                paper whose breakdown never got written. */}
            <div
              style={{
                flex: 1,
                minWidth: '14rem',
                display: isWriting || perPart.length === 0 ? 'none' : undefined,
              }}
            >
              <p className="eyebrow">By {result.test.module === 'READING' ? 'passage' : 'part'}</p>
              <ul className="partbars">
                {perPart.map((part) => (
                  <li key={part.index}>
                    <span className="mono">{part.index}</span>
                    <span
                      className="partbars__track"
                      role="img"
                      aria-label={`${part.correct} of ${part.total} correct`}
                    >
                      <span
                        className="partbars__fill"
                        style={{ width: `${(part.correct / part.total) * 100}%` }}
                      />
                    </span>
                    <span className="mono">
                      {part.correct}/{part.total}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="field__hint" style={{ marginTop: 'var(--s-4)' }}>
            {isWriting
              ? 'Judged against the four official criteria by a person, with Task 2 weighted double: (Task 1 + 2 × Task 2) ÷ 3.'
              : `Converted with the published ${result.bandTable} table. Band conversions are indicative — the real exam adjusts them slightly between versions to keep difficulty comparable.`}
          </p>
        </section>

        {isWriting && !isStudent && (
          <section className="stack">
            <div>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <p className="eyebrow">{result.awaitingMarking ? 'Mark this paper' : 'Marks'}</p>
                {canReview && !result.awaitingMarking && reviewLink}
              </div>
              <p className="muted" style={{ maxWidth: '62ch' }}>
                Read each task and score the four criteria. The band updates as you choose, so you
                can see what a judgement costs before you commit to it.
              </p>
            </div>
            <WritingMarker
              resultId={result.id}
              essays={result.essays ?? {}}
              scriptsClearedAt={result.scriptsClearedAt}
              scriptWords={result.scriptWords}
              existing={result.writingMarks}
              onMarked={() => {
                try {
                  sessionStorage.setItem(JUST_MARKED, result.id);
                } catch {
                  // Marked all the same; only the celebration is lost.
                }
                window.location.reload();
              }}
            />
          </section>
        )}

        {isWriting && isStudent && (
          <section className="stack">
            {/* The task beside the essay is on the review, not here. */}
            {canReview && <div className="row" style={{ justifyContent: 'flex-end' }}>{reviewLink}</div>}
            {/*
              * Said before it happens, while the Copy buttons are still there.
              * The essays go two days after the result reached the candidate;
              * the bands, the criteria and the teacher's comments stay.
              */}
            {!result.scriptsClearedAt &&
              clearsAt(result.markedAt, result.releasedAt) &&
              Object.values(result.essays ?? {}).some((e) => e?.trim()) && (
                <Banner tone="info">
                  Your writing is kept until{' '}
                  <strong>{formatDate(clearsAt(result.markedAt, result.releasedAt)!.toISOString())}</strong>.
                  Copy it into a document if you want to keep it — your bands and your teacher's
                  comments stay here after that.
                </Banner>
              )}
            {(Array.isArray(result.writingMarks) ? result.writingMarks : []).map((m) => (
              <article key={m.task} className="panel stack">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <h2 className="panel__title" style={{ marginBottom: 0 }}>
                    Task {m.task}
                  </h2>
                  <span className="mono" style={{ fontWeight: 700 }}>{band(m.band)}</span>
                </div>
                <div className="ledger__wrap">
                  <table className="ledger">
                    <tbody>
                      {CRITERIA.map((c) => (
                        <tr key={c.key}>
                          <td>{c.label}</td>
                          <td className="num" style={{ fontWeight: 600 }}>
                            {band(m.criteria[c.key])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {m.comments && (
                  <div>
                    <p className="eyebrow">Your teacher wrote</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{m.comments}</p>
                  </div>
                )}
                <div>
                  <div
                    className="row"
                    style={{ justifyContent: 'space-between', alignItems: 'baseline' }}
                  >
                    <p className="eyebrow" style={{ marginBottom: 0 }}>
                      What you wrote
                    </p>
                    {/*
                      * The whole script on the clipboard, paragraph breaks and
                      * all, so it pastes into Word as it was written — rather
                      * than dragging a selection down a long panel.
                      */}
                    {result.essays?.[m.task]?.trim() && (
                      <div className="row" style={{ gap: 'var(--s-3)', alignItems: 'baseline' }}>
                        <span className="muted mono">
                          {wordCount(result.essays[m.task]!)} words
                        </span>
                        <button
                          className="btn btn--ghost"
                          onClick={() => void copyMine(m.task, result.essays![m.task]!)}
                        >
                          {copied === m.task ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    )}
                  </div>
                  <article
                    className="marking__essay"
                    id={`my-essay-${m.task}`}
                    style={{ maxHeight: 'none' }}
                  >
                    {result.essays?.[m.task]?.trim() ? (
                      result.essays[m.task]!
                        .split(/\n{2,}/)
                        .map((para, i) => <p key={i}>{para}</p>)
                    ) : result.scriptsClearedAt ? (
                      /*
                       * The script is gone, and saying so is better than an
                       * empty panel that reads as though nothing was handed in.
                       * The length stays, because it is the part a candidate
                       * can still act on: under 150 words on Task 1 or 250 on
                       * Task 2 costs marks on its own.
                       */
                      <p className="muted">
                        Your script ran to {result.scriptWords?.[m.task] ?? 0} words. It was cleared
                        on {formatDate(result.scriptsClearedAt)}, two days after your result was
                        released — use the Copy button next time to keep your own copy.
                      </p>
                    ) : (
                      <p className="muted">Nothing was written for this task.</p>
                    )}
                  </article>
                </div>
              </article>
            ))}
          </section>
        )}

        {!isWriting && (
        <section className="stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <p className="eyebrow">Every question</p>
            <div className="row" style={{ gap: 'var(--s-5)' }}>
              <label className="row" style={{ gap: 'var(--s-2)' }}>
                <input
                  type="checkbox"
                  checked={onlyWrong}
                  onChange={(e) => setOnlyWrong(e.target.checked)}
                />
                <span>Show only the {wrongCount} not correct</span>
              </label>
              {canReview && reviewLink}
            </div>
          </div>

          <div className="ledger__wrap">
            <table className="ledger ledger--answers">
              <thead>
                <tr>
                  <th className="num">Q</th>
                  <th className="num ledger__partCol">Part</th>
                  <th>{isStudent ? 'Your answer' : 'Answer given'}</th>
                  {/* The key is staff's: a student sees whether their answer
                      was accepted, not what would have been. */}
                  {!isStudent && <th>Accepted</th>}
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {marks.map((mark, i) => [
                  /* On a phone the part is a heading where it begins rather
                     than a column of its own; on a wide screen the column
                     stays and this row is not drawn. */
                  mark.part !== null && mark.part !== marks[i - 1]?.part ? (
                    <tr key={`part-${mark.part}-${i}`} className="ledger__group">
                      <td colSpan={isStudent ? 4 : 5}>Part {mark.part}</td>
                    </tr>
                  ) : null,
                  <tr key={mark.questionNumber}>
                    <td className="num">{mark.questionNumber}</td>
                    <td className="num muted ledger__partCol">{mark.part ?? '—'}</td>
                    <td className="mono">
                      {mark.given || <span className="muted">— nothing written —</span>}
                    </td>
                    {!isStudent && <td className="mono muted">{mark.accepted.join(' · ')}</td>}
                    <td>
                      <span className={`tag ${mark.correct ? 'tag--on' : 'tag--off'}`}>
                        {REASON_LABEL[mark.reason]}
                      </span>
                    </td>
                  </tr>,
                ])}
              </tbody>
            </table>
          </div>
        </section>
        )}
      </div>
    </>
  );
}
